import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { notifySaveError } from "../../../../components/SaveErrorNotice";
import { useAppState } from "../../../../contexts/App";
import { usePreviewSite } from "../../../../contexts/PreviewSite";
import { useSite } from "../../../../contexts/Site";
import { useCurrentPage } from "../../../../hooks/CurrentPage";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import { getPageSections } from "../../../../model/pages";
import { Section, Site } from "../../../../model/site";
import { api } from "../../../../services/api";
import { clone, deepEqual } from "../../../../utils/objects";
import { uuidv4 } from "../../../../utils/uuid";

const getSectionById = <T extends Section>(
  sectionType: string,
  sectionId: string | null | undefined,
  site?: Site,
  pageId: string | null = null
) => {
  const section =
    site && getPageSections(site, pageId).find(({ type, id }) => type === sectionType && id === sectionId);
  return section
    ? { section, present: true }
    : {
        present: false,
        section: {
          id: uuidv4(),
          type: sectionType,
          active: true,
        } as T,
      };
};

/** Writes `section` into the sections being edited: the page `pageId`'s, or the home's. */
export const updateSite = (section: Section, site: Site, pageId: string | null = null) => {
  const _section = JSON.parse(
    JSON.stringify(section, (_, value) => (typeof value === "string" && !value.trim() ? undefined : value))
  );

  const sections = getPageSections(site, pageId);
  const index = sections.findIndex(({ type, id }) => type === section.type && id === section.id);
  if (index > -1) {
    sections[index] = { ..._section, active: true };
  } else {
    sections.push({ ..._section, active: true });
  }
};

export function useSection<T extends Section>(type: string, modify?: (newly: boolean, section: T) => T) {
  const navigate = useNavigate();
  const { site, setSite } = useSite();
  const { appState, setAppState } = useAppState();
  const sectionId = useQueryUrl().get("id");
  const { pageId } = useCurrentPage();

  const initialSectionState = useMemo(() => {
    const { section, present } = getSectionById<T>(type, sectionId, site, pageId);
    const safeSection = clone<T>(section);
    return modify ? modify(!present, safeSection) : safeSection;
  }, [sectionId, site, type, modify, pageId]);

  const [section, setSection] = useState(initialSectionState);

  // Mirror the section being edited into the live preview before it's saved
  const { setPreviewSite } = usePreviewSite();

  useEffect(() => {
    if (!site) {
      return;
    }
    const draftSite = clone<Site>(site);
    updateSite(section, draftSite, pageId);
    setPreviewSite(draftSite);
  }, [section, site, setPreviewSite, pageId]);

  useEffect(() => () => setPreviewSite(undefined), [setPreviewSite]);

  const saveSection = (updatedSection?: T) => {
    // the pages copied too: the section may be written inside one of them
    const copySite = { ...site, pages: site.pages?.map(page => ({ ...page, sections: [...page.sections] })) };
    updateSite(updatedSection || section, copySite, pageId);
    setSite(copySite);

    setAppState({ ...appState, editingSiteModified: true });
    // This site already exists (sections are only editable after Title's first
    // save), so this can't hit the free Keepos limit - just avoid an unhandled
    // rejection now that saveDraft returns a promise.
    api.saveDraft(appState.editingSiteId as string, copySite).catch(notifySaveError);

    navigate(-1);
  };

  const hasUnsavedChanges = () => !deepEqual(initialSectionState, section);

  return {
    section,
    setSection,
    saveSection,
    hasUnsavedChanges,
  } as {
    section: T;
    setSection: Dispatch<SetStateAction<T>>;
    saveSection: (t?: T) => void;
    hasUnsavedChanges: () => boolean;
  };
}

/**
 * Live preview for item editing screens (a link, product, FAQ or highlight): shows the
 * parent section with the item being edited applied, before either is saved.
 */
export function useItemPreview<I>(
  section: (Section & { items?: I[] }) | undefined,
  item: I,
  isSameItem: (existing: I) => boolean
) {
  const { site } = useSite();
  const { pageId } = useCurrentPage();
  const { setPreviewSite } = usePreviewSite();
  const isSameItemRef = useRef(isSameItem);
  isSameItemRef.current = isSameItem;

  useEffect(() => {
    if (!site || !section) {
      return;
    }
    const items = [...(section.items || [])];
    const index = items.findIndex(existing => isSameItemRef.current(existing));
    if (index >= 0) {
      items[index] = item;
    } else {
      items.push(item);
    }

    const draftSite = clone<Site>(site);
    updateSite({ ...section, items } as Section, draftSite, pageId);
    setPreviewSite(draftSite);
  }, [item, section, site, setPreviewSite, pageId]);

  useEffect(() => () => setPreviewSite(undefined), [setPreviewSite]);
}
